import { useCallback, useMemo, useState } from 'react';
import Button from '@standardsdigital/ui-components/Button';
import Heading, { HeadingSize } from '@standardsdigital/ui-components/Heading';
import Table, { type CellContext } from '@standardsdigital/ui-components/Table';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import ActionsCell from '@/components/global/EditableTableCells/ActionsCell/ActionsCell';
import {
  createEntryFormSchema,
  type EntryFormValidationContext,
} from './Configurator.schema';
import {
  createEntryRequest,
  emptyUpdateCustomSubscriptionConfigurationEntryRequest,
  createColumns,
  getActionsCellState,
  getEntriesToCompare,
  getPricePerUnitLabelKey,
  getRows,
} from './configuratorUtils';
import EditableNumberCell from '@/components/global/EditableTableCells/EditableNumberCell/EditableNumberCell';
import type {
  ConfigurationEntryDto,
  CreateCustomSubscriptionConfigurationEntryRequest,
  CustomSubscriptionConfigurationEntryType,
  UpdateCustomSubscriptionConfigurationEntryRequest,
} from '@/redux/services/generate/configurations/customSubscriptionConfigurations';
import styles from './Configurator.module.css';

type EntryField = keyof UpdateCustomSubscriptionConfigurationEntryRequest;

interface ConfiguratorProps {
  title: string;
  description: string;
  addButtonText: string;
  configurationId: string;
  entryType: CustomSubscriptionConfigurationEntryType;
  configurationEntries: ConfigurationEntryDto[];
  onCreateEntry: (
    configurationId: string,
    entry: CreateCustomSubscriptionConfigurationEntryRequest,
  ) => Promise<void>;
  onUpdateEntry: (
    configurationId: string,
    entryId: string,
    entry: UpdateCustomSubscriptionConfigurationEntryRequest,
  ) => Promise<void>;
  onDeleteEntry: (configurationId: string, entryId: string) => Promise<void>;
  isMutatingEntry: boolean;
}

function Configurator({
  title,
  description,
  addButtonText,
  configurationId,
  entryType,
  configurationEntries,
  onCreateEntry,
  onUpdateEntry,
  onDeleteEntry,
  isMutatingEntry,
}: Readonly<ConfiguratorProps>) {
  const { t } = useTranslation();
  const [editedRow, setEditedRow] = useState<number | undefined>(undefined);
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [shouldValidateOnChange, setShouldValidateOnChange] = useState(false);
  const entriesToCompare = useMemo(
    () =>
      getEntriesToCompare({
        configurationEntries,
        editedRow,
        isCreatingEntry,
      }),
    [configurationEntries, editedRow, isCreatingEntry],
  );

  const {
    control,
    reset,
    setValue,
    clearErrors,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<
    UpdateCustomSubscriptionConfigurationEntryRequest,
    EntryFormValidationContext
  >({
    defaultValues: emptyUpdateCustomSubscriptionConfigurationEntryRequest,
    resolver: yupResolver(createEntryFormSchema()),
    context: {
      entriesToCompare,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const editedEntryValues = useWatch({
    control,
    defaultValue: emptyUpdateCustomSubscriptionConfigurationEntryRequest,
  }) as UpdateCustomSubscriptionConfigurationEntryRequest;

  const resetEditing = useCallback(() => {
    setEditedRow(undefined);
    setIsCreatingEntry(false);
    setShouldValidateOnChange(false);
    clearErrors();
    reset(emptyUpdateCustomSubscriptionConfigurationEntryRequest);
  }, [clearErrors, reset]);

  const setEditedValue = useCallback(
    (key: EntryField, value: number) => {
      setValue(key, value, {
        shouldDirty: true,
        shouldValidate: shouldValidateOnChange,
      });

      if (shouldValidateOnChange && (key === 'from' || key === 'to')) {
        void trigger(['from', 'to']);
      }
    },
    [setValue, shouldValidateOnChange, trigger],
  );

  const saveRow = useCallback(
    async (row: ConfigurationEntryDto, rowIndex: number) => {
      if (editedRow === undefined || editedRow !== rowIndex) {
        return;
      }

      setShouldValidateOnChange(true);
      const isSchemaValid = await trigger();

      if (!isSchemaValid) {
        return;
      }

      const request = createEntryRequest(getValues());

      try {
        if (isCreatingEntry) {
          await onCreateEntry(configurationId, {
            ...request,
            type: entryType,
          });
        } else {
          await onUpdateEntry(configurationId, row.id, request);
        }

        resetEditing();
      } catch {
        // Errors are handled in middleware.
      }
    },
    [
      configurationId,
      editedRow,
      entryType,
      getValues,
      isCreatingEntry,
      onCreateEntry,
      onUpdateEntry,
      resetEditing,
      trigger,
    ],
  );

  const editRow = useCallback(
    (rowIndex: number) => {
      const rowToEdit = configurationEntries[rowIndex];

      if (rowToEdit === undefined) {
        return;
      }

      reset(createEntryRequest(rowToEdit));
      setIsCreatingEntry(false);
      setShouldValidateOnChange(false);
      clearErrors();
      setEditedRow(rowIndex);
    },
    [clearErrors, configurationEntries, reset],
  );

  const createEntry = useCallback(() => {
    reset(emptyUpdateCustomSubscriptionConfigurationEntryRequest);
    setIsCreatingEntry(true);
    setShouldValidateOnChange(false);
    clearErrors();
    setEditedRow(configurationEntries.length);
  }, [clearErrors, configurationEntries.length, reset]);

  const rows = useMemo(
    () =>
      getRows({
        configurationEntries,
        editedEntryValues,
        editedRow,
        entryType,
        isCreatingEntry,
      }),
    [configurationEntries, editedEntryValues, editedRow, entryType, isCreatingEntry],
  );

  const fromLabel = t('customSubscriptionConfiguration.columns.from');
  const toLabel = t('customSubscriptionConfiguration.columns.to');
  const pricePerUnitLabel = t(getPricePerUnitLabelKey(entryType));

  const renderEditableNumberCell = useCallback(
    (
      cell: CellContext<ConfigurationEntryDto, number>,
      name: EntryField,
      label: string,
    ) => {
      const message = errors[name]?.message;

      return (
        <EditableNumberCell
          isEditing={editedRow === cell.row.index}
          value={cell.getValue()}
          label={label}
          name={name}
          hasError={errors[name] !== undefined}
          message={message ? t(message as never) : undefined}
          onValueChange={(value) => setEditedValue(name, value)}
        />
      );
    },
    [editedRow, errors, setEditedValue, t],
  );

  const renderActionsCell = useCallback(
    (cell: CellContext<ConfigurationEntryDto, unknown>) => {
      const { isEditingCurrentRow, isAnotherRowEditing, isUnsavedRow } =
        getActionsCellState({
          editedRow,
          rowIndex: cell.row.index,
          isCreatingEntry,
        });

      return (
        <ActionsCell
          isEditing={isEditingCurrentRow}
          isAnotherRowEditing={isAnotherRowEditing}
          onEdit={() => editRow(cell.row.index)}
          onCancel={resetEditing}
          onSave={() => {
            void saveRow(cell.row.original, cell.row.index);
          }}
          onDelete={() => {
            void onDeleteEntry(configurationId, cell.row.original.id);
          }}
          deleteConfirmationTitle={t(
            'customSubscriptionConfiguration.deleteEntryTitle',
          )}
          deleteConfirmationDescription={t(
            'customSubscriptionConfiguration.deleteEntryDescription',
          )}
          deleteButtonText={t('customSubscriptionConfiguration.removeEntry')}
          isBusy={isMutatingEntry}
          disableDelete={isUnsavedRow}
        />
      );
    },
    [
      configurationId,
      editRow,
      editedRow,
      isCreatingEntry,
      isMutatingEntry,
      onDeleteEntry,
      resetEditing,
      saveRow,
      t,
    ],
  );

  const columns = useMemo(
    () =>
      createColumns({
        fromLabel,
        toLabel,
        pricePerUnitLabel,
        actionsLabel: t('customSubscriptionConfiguration.columns.actions'),
        renderEditableNumberCell,
        renderActionsCell,
      }),
    [
      fromLabel,
      pricePerUnitLabel,
      renderActionsCell,
      renderEditableNumberCell,
      t,
      toLabel,
    ],
  );

  return (
    <section className={styles.configurator}>
      <div>
        <Heading size={HeadingSize.Xs} level={3}>
          {title}
        </Heading>
        <p className={styles.description}>{description}</p>
      </div>
      {configurationEntries.length === 0 && !isCreatingEntry ? (
        <div className={styles.emptyState}>
          <p>{t('customSubscriptionConfiguration.noEntriesFound')}</p>
        </div>
      ) : (
        <Table
          aria-label={title}
          columns={columns}
          data={rows}
          className={styles.table}
          tableWidth="100%"
        />
      )}
      <div className={styles.actionsRow}>
        <Button
          type="button"
          onClick={createEntry}
          disabled={editedRow !== undefined || isMutatingEntry}
        >
          {addButtonText}
        </Button>
      </div>
    </section>
  );
}

export default Configurator;
